/**
 * Waltero Performance Optimization Utilities
 * 
 * Collection of utilities for optimizing performance in the Waltero IoT Dashboard,
 * with special focus on mobile devices and slower connections.
 * 
 * Features:
 * - Lazy loading and code splitting helpers
 * - Image optimization and lazy loading
 * - Bundle analysis and optimization
 * - Memory management utilities
 * - Network-aware loading strategies
 * - Performance monitoring and metrics
 */

import { lazy, Suspense, useState, useEffect, useRef } from 'react';

/**
 * Lazy Loading Utilities
 */

// Enhanced lazy loading with error boundaries and loading states
export const createLazyComponent = (
  importFunc,
  options = {}
) => {
  const {
    fallback = null,
    errorFallback = null,
    preload = false,
    retryDelay = 1000,
    maxRetries = 3
  } = options;
  
  let componentPromise = null;
  let retryCount = 0;
  
  const LazyComponent = lazy(() => {
    if (!componentPromise) {
      componentPromise = importFunc().catch((error) => {
        console.error('Lazy component loading failed:', error);
        
        // Retry logic
        if (retryCount < maxRetries) {
          retryCount++;
          componentPromise = null;
          
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              createLazyComponent(importFunc, options);
              resolve();
            }, retryDelay * retryCount);
          });
        }
        
        // Return error fallback component
        if (errorFallback) {
          return { default: errorFallback };
        }
        
        throw error;
      });
    }
    
    return componentPromise;
  });
  
  // Preload component if requested
  if (preload) {
    const timer = setTimeout(() => {
      importFunc();
      clearTimeout(timer);
    }, 100);
  }
  
  return LazyComponent;
};

// Component wrapper with suspense and error boundary
export const LazyWrapper = ({ 
  children, 
  fallback = <div>Loading...</div>,
  errorFallback = <div>Failed to load component</div>
}) => (
  <Suspense fallback={fallback}>
    {children}
  </Suspense>
);

/**
 * Image Optimization Utilities
 */

// Lazy image loading with intersection observer
export class LazyImage {
  constructor(options = {}) {
    this.options = {
      rootMargin: '50px',
      threshold: 0.1,
      enableRetina: true,
      placeholder: 'data:image/svg+xml,',
      ...options
    };
    
    this.observer = null;
    this.init();
  }
  
  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        {
          rootMargin: this.options.rootMargin,
          threshold: this.options.threshold
        }
      );
    }
  }
  
  observe(element) {
    if (this.observer) {
      this.observer.observe(element);
    } else {
      // Fallback for browsers without intersection observer
      this.loadImage(element);
    }
  }
  
  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.loadImage(entry.target);
        this.observer.unobserve(entry.target);
      }
    });
  }
  
  loadImage(img) {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    
    if (src) {
      img.src = src;
    }
    
    if (srcset) {
      img.srcset = srcset;
    }
    
    img.classList.remove('lazy');
    img.classList.add('loaded');
  }
  
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// React hook for lazy images
export const useLazyImage = (src, options = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const imageRef = useRef(null);
  
  useEffect(() => {
    const lazyImage = new LazyImage({
      ...options,
      onLoad: () => setIsLoaded(true),
      onError: () => setIsError(true)
    });
    
    if (imageRef.current) {
      imageRef.current.dataset.src = src;
      lazyImage.observe(imageRef.current);
    }
    
    return () => lazyImage.destroy();
  }, [src, options]);
  
  return { imageRef, isLoaded, isError };
};

/**
 * Memory Management Utilities
 */

// Memory usage monitor
export class MemoryMonitor {
  constructor() {
    this.measurements = [];
    this.warningThreshold = 50 * 1024 * 1024; // 50MB
    this.criticalThreshold = 100 * 1024 * 1024; // 100MB
  }
  
  measure() {
    if ('memory' in performance) {
      const memory = performance.memory;
      const measurement = {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        timestamp: Date.now()
      };
      
      this.measurements.push(measurement);
      
      // Keep only last 100 measurements
      if (this.measurements.length > 100) {
        this.measurements.shift();
      }
      
      this.checkThresholds(measurement);
      return measurement;
    }
    
    return null;
  }
  
  checkThresholds(measurement) {
    const { usedJSHeapSize } = measurement;
    
    if (usedJSHeapSize > this.criticalThreshold) {
      console.warn('Critical memory usage detected:', usedJSHeapSize);
      this.triggerMemoryCleanup();
    } else if (usedJSHeapSize > this.warningThreshold) {
      console.warn('High memory usage:', usedJSHeapSize);
    }
  }
  
  triggerMemoryCleanup() {
    // Suggest garbage collection if available
    if (window.gc) {
      window.gc();
    }
    
    // Clear image caches
    this.clearImageCaches();
    
    // Dispatch memory warning event
    window.dispatchEvent(new CustomEvent('memoryWarning', {
      detail: { usedMemory: this.getLatestMeasurement()?.usedJSHeapSize }
    }));
  }
  
  clearImageCaches() {
    // Clear blob URLs that might be cached
    const images = document.querySelectorAll('img[src^="blob:"]');
    images.forEach(img => {
      URL.revokeObjectURL(img.src);
    });
  }
  
  getLatestMeasurement() {
    return this.measurements[this.measurements.length - 1];
  }
  
  getAverageUsage(samples = 10) {
    const recent = this.measurements.slice(-samples);
    const total = recent.reduce((sum, m) => sum + m.usedJSHeapSize, 0);
    return total / recent.length;
  }
}

/**
 * Network-Aware Loading
 */

// Network condition detector
export class NetworkMonitor {
  constructor() {
    this.connection = null;
    this.listeners = new Set();
    this.init();
  }
  
  init() {
    // Navigator connection API
    this.connection = navigator.connection || 
                      navigator.mozConnection || 
                      navigator.webkitConnection;
    
    if (this.connection) {
      this.connection.addEventListener('change', this.handleChange.bind(this));
    }
    
    // Online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }
  
  handleChange() {
    this.notifyListeners({
      type: 'connection-change',
      connection: this.getConnectionInfo()
    });
  }
  
  handleOnline() {
    this.notifyListeners({
      type: 'online',
      connection: this.getConnectionInfo()
    });
  }
  
  handleOffline() {
    this.notifyListeners({
      type: 'offline',
      connection: this.getConnectionInfo()
    });
  }
  
  getConnectionInfo() {
    return {
      online: navigator.onLine,
      effectiveType: this.connection?.effectiveType || 'unknown',
      downlink: this.connection?.downlink || 0,
      rtt: this.connection?.rtt || 0,
      saveData: this.connection?.saveData || false
    };
  }
  
  isSlowConnection() {
    const info = this.getConnectionInfo();
    return info.effectiveType === 'slow-2g' || 
           info.effectiveType === '2g' ||
           info.saveData ||
           info.downlink < 0.5;
  }
  
  isFastConnection() {
    const info = this.getConnectionInfo();
    return info.effectiveType === '4g' && info.downlink > 10;
  }
  
  addListener(callback) {
    this.listeners.add(callback);
  }
  
  removeListener(callback) {
    this.listeners.delete(callback);
  }
  
  notifyListeners(event) {
    this.listeners.forEach(callback => callback(event));
  }
}

// React hook for network monitoring
export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState(() => {
    const monitor = new NetworkMonitor();
    return monitor.getConnectionInfo();
  });
  
  useEffect(() => {
    const monitor = new NetworkMonitor();
    
    const handleNetworkChange = (event) => {
      setNetworkStatus(event.connection);
    };
    
    monitor.addListener(handleNetworkChange);
    
    return () => {
      monitor.removeListener(handleNetworkChange);
    };
  }, []);
  
  return networkStatus;
};

/**
 * Performance Monitoring
 */

// Performance metrics collector
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.observers = new Map();
    this.init();
  }
  
  init() {
    // Performance observer for paint timing
    if ('PerformanceObserver' in window) {
      this.observePaintTiming();
      this.observeLayoutShift();
      this.observeLargestContentfulPaint();
    }
    
    // Listen for page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }
  
  observePaintTiming() {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        this.metrics.set(entry.name, entry.startTime);
      });
    });
    
    observer.observe({ entryTypes: ['paint'] });
    this.observers.set('paint', observer);
  }
  
  observeLayoutShift() {
    const observer = new PerformanceObserver((list) => {
      let clsValue = 0;
      list.getEntries().forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      
      this.metrics.set('cumulative-layout-shift', clsValue);
    });
    
    observer.observe({ entryTypes: ['layout-shift'] });
    this.observers.set('layout-shift', observer);
  }
  
  observeLargestContentfulPaint() {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.metrics.set('largest-contentful-paint', lastEntry.startTime);
    });
    
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
    this.observers.set('lcp', observer);
  }
  
  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.reportMetrics();
    }
  }
  
  markStart(name) {
    performance.mark(`${name}-start`);
  }
  
  markEnd(name) {
    performance.mark(`${name}-end`);
    performance.measure(name, `${name}-start`, `${name}-end`);
    
    const measure = performance.getEntriesByName(name, 'measure')[0];
    this.metrics.set(name, measure.duration);
    
    return measure.duration;
  }
  
  getMetric(name) {
    return this.metrics.get(name);
  }
  
  getAllMetrics() {
    return Object.fromEntries(this.metrics);
  }
  
  reportMetrics() {
    const metrics = this.getAllMetrics();
    console.log('Performance Metrics:', metrics);
    
    // Send to analytics if available
    if (window.gtag) {
      Object.entries(metrics).forEach(([name, value]) => {
        window.gtag('event', 'timing_complete', {
          name,
          value: Math.round(value)
        });
      });
    }
  }
  
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.metrics.clear();
  }
}

/**
 * Bundle Analysis Utilities
 */

// Webpack bundle analyzer helper
export const analyzeBundleSize = () => {
  console.log('Bundle analysis not available - webpack-bundle-analyzer not installed');
  return Promise.resolve();
  
  // TODO: Install webpack-bundle-analyzer as dev dependency to enable bundle analysis
  /*
  if (process.env.NODE_ENV !== 'production') {
    console.log('Bundle analysis available in production build');
    return Promise.resolve();
  }
  
  // Dynamic import for bundle analyzer
  return import('webpack-bundle-analyzer/lib/analyzer')
    .then(({ analyzer }) => {
      analyzer.start();
    })
    .catch((error) => {
      console.log('Bundle analyzer not available:', error);
    });
  */
};

// Chunk loading monitor
export const monitorChunkLoading = () => {
  const chunkPromises = new Map();
  
  // Override dynamic import to track loading
  const originalImport = window.__webpack_require__.e;
  
  window.__webpack_require__.e = function(chunkId) {
    const start = performance.now();
    
    const promise = originalImport.call(this, chunkId).then(
      (result) => {
        const duration = performance.now() - start;
        console.log(`Chunk ${chunkId} loaded in ${duration}ms`);
        chunkPromises.delete(chunkId);
        return result;
      },
      (error) => {
        const duration = performance.now() - start;
        console.error(`Chunk ${chunkId} failed to load after ${duration}ms:`, error);
        chunkPromises.delete(chunkId);
        throw error;
      }
    );
    
    chunkPromises.set(chunkId, promise);
    return promise;
  };
  
  return {
    getPendingChunks: () => Array.from(chunkPromises.keys()),
    getChunkPromise: (chunkId) => chunkPromises.get(chunkId)
  };
};

/**
 * Debounce and Throttle Utilities
 */

// Enhanced debounce with immediate option
export const debounce = (func, wait, options = {}) => {
  let timeout;
  let result;
  const { immediate = false, maxWait = null } = options;
  
  let lastCallTime;
  let lastInvokeTime = 0;
  
  const invokeFunc = (time) => {
    const args = lastArgs;
    const thisArg = lastThis;
    
    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  };
  
  const startTimer = (pendingFunc, wait) => {
    timeout = setTimeout(pendingFunc, wait);
  };
  
  const shouldInvoke = (time) => {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    
    return (lastCallTime === undefined || 
            timeSinceLastCall >= wait ||
            timeSinceLastCall < 0 ||
            (maxWait !== null && timeSinceLastInvoke >= maxWait));
  };
  
  const timerExpired = () => {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timeout = startTimer(timerExpired, remainingWait(time));
  };
  
  const trailingEdge = (time) => {
    timeout = undefined;
    if (lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  };
  
  const remainingWait = (time) => {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;
    
    return maxWait !== null 
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  };
  
  let lastArgs, lastThis;
  
  const debounced = function(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);
    
    lastArgs = args;
    lastThis = this;
    lastCallTime = time;
    
    if (isInvoking) {
      if (timeout === undefined) {
        if (immediate) {
          const callNow = !timeout;
          timeout = setTimeout(() => { timeout = undefined; }, wait);
          if (callNow) result = func.apply(this, args);
        } else {
          lastInvokeTime = time;
          timeout = startTimer(timerExpired, wait);
        }
        return result;
      }
      if (maxWait !== null) {
        timeout = startTimer(timerExpired, wait);
        return invokeFunc(time);
      }
    }
    if (timeout === undefined) {
      timeout = startTimer(timerExpired, wait);
    }
    return result;
  };
  
  debounced.cancel = () => {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timeout = undefined;
  };
  
  debounced.flush = () => {
    return timeout === undefined ? result : trailingEdge(Date.now());
  };
  
  return debounced;
};

// Throttle with leading and trailing options
export const throttle = (func, wait, options = {}) => {
  const { leading = true, trailing = true } = options;
  return debounce(func, wait, {
    leading,
    maxWait: wait,
    trailing
  });
};

// Create singleton instances
export const memoryMonitor = new MemoryMonitor();
export const networkMonitor = new NetworkMonitor();
export const performanceMonitor = new PerformanceMonitor();

// Auto-start monitoring in development
if (process.env.NODE_ENV === 'development') {
  // Monitor memory every 30 seconds
  setInterval(() => {
    memoryMonitor.measure();
  }, 30000);
  
  // Log performance metrics on page hide
  window.addEventListener('beforeunload', () => {
    performanceMonitor.reportMetrics();
  });
}

export default {
  createLazyComponent,
  LazyWrapper,
  LazyImage,
  useLazyImage,
  MemoryMonitor,
  NetworkMonitor,
  useNetworkStatus,
  PerformanceMonitor,
  analyzeBundleSize,
  monitorChunkLoading,
  debounce,
  throttle,
  memoryMonitor,
  networkMonitor,
  performanceMonitor
};